/**
 * Per-project «guided vs quick» create preference (W12 Part B, Task B1).
 *
 * The wizard always opens first for a project that has never switched; the
 * header switch is the ONLY way into quick create, and the choice is remembered
 * per project (owner ruling 2026-09-10). Storage is best-effort: a browser that
 * refuses localStorage falls back to the guided default instead of throwing.
 */

import { useCallback, useRef, useState } from 'react';

export type StrategyCreateMode = 'guided' | 'quick';

export const STRATEGY_CREATE_MODE_STORAGE_KEY = (projectId: string): string =>
  `segmently.strategyCreateMode.${projectId}`;

const DEFAULT_MODE: StrategyCreateMode = 'guided';

const readMode = (projectId: string): StrategyCreateMode => {
  try {
    const stored = window.localStorage.getItem(STRATEGY_CREATE_MODE_STORAGE_KEY(projectId));
    return stored === 'quick' || stored === 'guided' ? stored : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
};

export function useStrategyCreateMode(
  projectId: string,
): [StrategyCreateMode, (mode: StrategyCreateMode) => void] {
  const [mode, setMode] = useState<StrategyCreateMode>(() => readMode(projectId));

  // The preference belongs to the PROJECT: a reused hook instance pointed at a
  // different project re-reads instead of leaking the previous project's choice.
  const lastProjectId = useRef(projectId);
  if (lastProjectId.current !== projectId) {
    lastProjectId.current = projectId;
    setMode(readMode(projectId));
  }

  const persist = useCallback((next: StrategyCreateMode) => {
    setMode(next);
    try {
      window.localStorage.setItem(STRATEGY_CREATE_MODE_STORAGE_KEY(projectId), next);
    } catch {
      // Storage blocked (private mode, quota) — the choice still applies to
      // this dialog session, it just will not be remembered.
    }
  }, [projectId]);

  return [mode, persist];
}
